import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarOff, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { SlotGrid } from "../SlotGrid";

const API = process.env.REACT_APP_BACKEND_URL;
const LOCATION_ID = "loc_saturn_duleek";

/**
 * Admin "Block Calendar" section.
 *
 * Purpose: let staff/owners take a specific day OFF the customer-facing
 * booking grid (holiday / staff sick / personal day) — either as a whole
 * day or a specific time range — and remove those blocks with one click.
 *
 * Data flow:
 *   - GET /api/bookings/slots/{loc}/{date}         → underlying slot grid
 *   - GET /api/admin/schedule-blocks/?date_from&date_to&location_id
 *   - POST /api/admin/schedule-blocks/             → create block
 *   - DELETE /api/admin/schedule-blocks/{id}       → remove block
 *
 * Click behaviour on the slot grid (mode="admin"):
 *   - Available slot            → create a 30-min range block for that slot
 *   - Blocked slot (from admin) → delete that block (round-trip toggle)
 *   - Occupied by a real booking → no-op (server would reject anyway)
 */
export default function BlockCalendarSection() {
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [slots, setSlots] = useState([]);
  const [dayClosedReason, setDayClosedReason] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reasonInput, setReasonInput] = useState("");

  const reload = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const [slotsRes, blocksRes] = await Promise.all([
        axios.get(`${API}/api/bookings/slots/${LOCATION_ID}/${selectedDate}`),
        axios.get(
          `${API}/api/admin/schedule-blocks/?location_id=${LOCATION_ID}&date_from=${selectedDate}&date_to=${selectedDate}`
        ),
      ]);

      const slotData = slotsRes.data || {};
      setDayClosedReason(slotData.available === false ? slotData.reason : null);
      const rawSlots = slotData.slots || [];
      const rawBlocks = blocksRes.data?.blocks || [];

      // Decorate slots with block metadata client-side. The public slots
      // endpoint deliberately omits `blocked` / `block_reason` (customer
      // must only see "Not available"). The admin dashboard reconstructs
      // this locally by joining against the fetched blocks list.
      const decoratedSlots = rawSlots.map((s) => {
        const covering = rawBlocks.find((b) => {
          if (b.start_time === null) return true;
          return b.start_time <= s.time && s.time < b.end_time;
        });
        return covering
          ? { ...s, blocked: true, block_reason: covering.reason }
          : s;
      });
      setSlots(decoratedSlots);
      setBlocks(rawBlocks);
    } catch (e) {
      toast.error("Failed to load calendar data");
      setSlots([]);
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Find the admin-block that covers a given slot (if any)
  const blockCoveringSlot = (slotTime) => {
    return blocks.find((b) => {
      if (b.start_time === null) return true; // whole-day block
      return b.start_time <= slotTime && slotTime < b.end_time;
    });
  };

  const handleSlotClick = async (slot) => {
    if (!slot.available && slot.blocked) {
      // Click blocked slot → remove that block
      const b = blockCoveringSlot(slot.time);
      if (!b) return;
      try {
        await axios.delete(`${API}/api/admin/schedule-blocks/${b.block_id}`);
        toast.success(`Removed block: ${b.reason}`);
        reload();
      } catch (e) {
        toast.error("Could not remove block");
      }
      return;
    }
    if (!slot.available) {
      toast.info("This slot is already booked — cannot block on top of a real booking.");
      return;
    }
    // Available slot → create a 30-min range block
    // Compute end-time (slot start + 30 min)
    const [h, m] = slot.time.split(":").map(Number);
    const endMin = m + 30;
    const endTime = `${String(h + Math.floor(endMin / 60)).padStart(2, "0")}:${String(
      endMin % 60
    ).padStart(2, "0")}`;

    const reason = reasonInput.trim() || "Blocked by admin";
    try {
      await axios.post(`${API}/api/admin/schedule-blocks/`, {
        location_id: LOCATION_ID,
        date: selectedDate,
        start_time: slot.time,
        end_time: endTime,
        reason,
      });
      toast.success(`Blocked ${slot.time}-${endTime}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create block");
    }
  };

  const handleBlockWholeDay = async () => {
    const reason = reasonInput.trim() || "Closed (admin)";
    try {
      await axios.post(`${API}/api/admin/schedule-blocks/`, {
        location_id: LOCATION_ID,
        date: selectedDate,
        start_time: null,
        end_time: null,
        reason,
      });
      toast.success(`Blocked whole day: ${selectedDate}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not block day");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await axios.delete(`${API}/api/admin/schedule-blocks/${blockId}`);
      toast.success("Block removed");
      reload();
    } catch (e) {
      toast.error("Could not remove block");
    }
  };

  return (
    <div className="space-y-6" data-testid="block-calendar-section">
      <Card className="bg-saturn-slate border-slate-800">
        <CardHeader>
          <CardTitle className="text-white font-orbitron flex items-center gap-2">
            <CalendarOff className="w-5 h-5 text-amber-400" />
            Block Calendar
          </CardTitle>
          <p className="text-sm text-slate-400 font-rajdhani">
            Take a day (or slot) off the booking grid — for holidays, staff sick, or personal days.
            Click any available slot to block it; click a blocked slot to unblock it.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Date + reason + whole-day action */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label htmlFor="block-date" className="text-slate-300 font-rajdhani">
                Date
              </Label>
              <Input
                id="block-date"
                type="date"
                value={selectedDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="block-cal-date-input"
              />
            </div>
            <div>
              <Label htmlFor="block-reason" className="text-slate-300 font-rajdhani">
                Reason (applied to next block created)
              </Label>
              <Input
                id="block-reason"
                type="text"
                placeholder="e.g. Holiday · Staff sick · Personal"
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="block-cal-reason-input"
                maxLength={200}
              />
            </div>
            <Button
              onClick={handleBlockWholeDay}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="block-cal-whole-day-btn"
            >
              <CalendarOff className="w-4 h-4 mr-2" /> Block Whole Day
            </Button>
          </div>

          {/* Slot grid */}
          <div>
            <h3 className="text-white font-orbitron mb-3">Slots on {selectedDate}</h3>
            <SlotGrid
              slots={slots}
              loading={loading}
              closedReason={dayClosedReason}
              emptyMessage="No slots for this day"
              onSlotClick={handleSlotClick}
              mode="admin"
              testIdPrefix="block-slot"
            />
          </div>
        </CardContent>
      </Card>

      {/* Existing blocks for this day */}
      <Card className="bg-saturn-slate border-slate-800">
        <CardHeader>
          <CardTitle className="text-white font-orbitron">
            Existing blocks on {selectedDate}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blocks.length === 0 ? (
            <p className="text-slate-400 font-rajdhani">No blocks on this day.</p>
          ) : (
            <ul className="space-y-2" data-testid="existing-blocks-list">
              {blocks.map((b) => (
                <li
                  key={b.block_id}
                  className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded-lg p-3"
                  data-testid={`block-row-${b.block_id}`}
                >
                  <div>
                    <div className="text-white font-rajdhani">
                      {b.start_time === null
                        ? "Whole day"
                        : `${b.start_time} – ${b.end_time}`}
                      <span className="ml-2 text-amber-400">· {b.reason}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      by {b.created_by} · {new Date(b.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteBlock(b.block_id)}
                    className="border-red-600 text-red-400 hover:bg-red-900/30"
                    data-testid={`delete-block-${b.block_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
